
import { supabase } from '@/integrations/supabase/client';

// Define the WhatsApp settings interface
export interface WhatsAppSettings {
  whatsapp_number: string;
  form_message: string;
  cta_message?: string;
  admin_message: string;
  eligibility_parameters?: {
    min_revenue: number;
    max_revenue: number;
    eligible_tax_regimes: string[];
  };
}

// Default WhatsApp number to use as a fallback
export const DEFAULT_WHATSAPP_NUMBER = '5511932169806';

// In-memory cache for WhatsApp settings
let cachedSettings: WhatsAppSettings | null = null;
let fetchPromise: Promise<WhatsAppSettings | null> | null = null;

// Default settings used as last resort
const defaultSettings: WhatsAppSettings = {
  whatsapp_number: DEFAULT_WHATSAPP_NUMBER,
  form_message: 'Olá! Acabei de acessar o site de vocês e gostaria de entender os próximos passos sobre a minha elegibilidade para a equiparação hospitalar',
  cta_message: 'Olá! Acabei de acessar o site de vocês e gostaria de entender os próximos passos sobre a minha elegibilidade para a equiparação hospitalar',
  admin_message: 'Olá, tenho interesse em sua solicitação'
};

/**
 * Singleton fetch - only makes one network request, ever.
 * Subsequent calls return the cached promise.
 */
export const fetchWhatsAppSettings = async (): Promise<WhatsAppSettings | null> => {
  // Return cached if available
  if (cachedSettings) return cachedSettings;

  // Return in-flight promise if already fetching
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('taxhealth_settings')
        .select('value')
        .eq('key', 'lead_magnet')
        .single();

      if (error || !data?.value) {
        return getCachedWhatsAppSettings();
      }

      if (Array.isArray(data.value) || typeof data.value !== 'object') {
        return getCachedWhatsAppSettings();
      }

      const jsonValue = data.value as Record<string, any>;

      if (!jsonValue.whatsapp_number || !jsonValue.form_message || !jsonValue.admin_message) {
        return getCachedWhatsAppSettings();
      }

      const settingsData: WhatsAppSettings = {
        whatsapp_number: String(jsonValue.whatsapp_number),
        form_message: String(jsonValue.form_message),
        cta_message: jsonValue.cta_message ? String(jsonValue.cta_message) : undefined,
        admin_message: String(jsonValue.admin_message),
        eligibility_parameters: jsonValue.eligibility_parameters ? {
          min_revenue: Number(jsonValue.eligibility_parameters.min_revenue || 0),
          max_revenue: Number(jsonValue.eligibility_parameters.max_revenue || 0),
          eligible_tax_regimes: Array.isArray(jsonValue.eligibility_parameters.eligible_tax_regimes)
            ? jsonValue.eligibility_parameters.eligible_tax_regimes.map(String)
            : []
        } : undefined
      };

      cachedSettings = settingsData;

      try {
        localStorage.setItem('whatsapp_settings', JSON.stringify(settingsData));
      } catch (_) { /* ignore */ }

      return settingsData;
    } catch {
      return getCachedWhatsAppSettings();
    }
  })();

  return fetchPromise;
};

/**
 * Gets cached WhatsApp settings from memory or localStorage
 */
export const getCachedWhatsAppSettings = (): WhatsAppSettings => {
  if (cachedSettings) return cachedSettings;

  try {
    const stored = localStorage.getItem('whatsapp_settings');
    if (stored) {
      const parsed = JSON.parse(stored) as WhatsAppSettings;
      if (parsed?.whatsapp_number) {
        cachedSettings = parsed;
        return parsed;
      }
    }
  } catch { /* ignore */ }

  return defaultSettings;
};

/**
 * Explicit init function — call after LCP to start fetching settings.
 */
export const initWhatsAppSettings = () => {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => { fetchWhatsAppSettings(); });
  } else {
    setTimeout(() => { fetchWhatsAppSettings(); }, 2000);
  }
};

/**
 * Gets WhatsApp number from the Calculator component's data attribute
 */
export const getWhatsAppNumberFromCalculator = (): string | null => {
  try {
    const calculatorElement = document.querySelector('#calculator');
    if (calculatorElement) {
      const whatsappNumber = calculatorElement.getAttribute('data-whatsapp-number');
      if (whatsappNumber) return whatsappNumber;
    }
  } catch { /* ignore */ }
  return null;
};
